import type { AlloyLegacyThemeDesignTokens, TabsDesignTokens } from '../types';
import type { Weave3TabsInput } from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getTabsTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): TabsDesignTokens => ({
  fontFamily: theme.typography.heading4.fontFamily,
  fontWeight: theme.typography.bodyMedium.fontWeight,
  fontSize: theme.typography.heading4.fontSize,
  lineHeight: theme.typography.heading4.lineHeight,

  marginHorizontal: '12px',
  marginBottom: undefined,
  paddingTop: '0px',
  paddingVertical: '6px',
  paddingHorizontal: '4px',
  paddingBottom: '6px',
  iconMargin: '4px',
  minWidth: '48px',

  borderBottom: undefined,

  default: {
    color: theme.tokens.colors.text.primary.value,
    iconColor: theme.tokens.colors.text.primary.value,
  },

  hover: {
    color: theme.tokens.colors.text.hover.value,
    iconColor: theme.tokens.colors.text.hover.value,
  },

  focus: {
    color: theme.tokens.colors.text.primary.value,
    iconColor: theme.tokens.colors.text.primary.value,
  },

  active: {
    color: theme.tokens.colors.text.primary.value,
    iconColor: theme.tokens.colors.text.primary.value,
    fontWeight: theme.typography.heading4.fontWeight,
  },

  disabled: {
    color: theme.tokens.colors.text.inactive.value,
    iconColor: theme.tokens.colors.icon.inactive.value,
  },

  indicator: {
    background: theme.tokens.colors.border.selected.value,
    height: '2px',
  },

  container: {
    borderBottomColor: theme.tokens.colors.border.subtle.value,
    borderBottomWidth: '1px',
  },

  focusShadow: {
    color: theme.colors.focusHalo,
    width: '2px',
    offset: '-3px',
  },
});

/**
 * `indicator` is deliberately absent. The animated slider it styles renders on
 * the Alloy path only (`!isWeave` in `Tabs`); Weave marks the selected tab with
 * `active.border-color` on the tab's own bottom border instead. Emitting a
 * plausible value for a box that never renders would just be data no reader can
 * trust — if the slider is ever enabled for Weave, map it then.
 */
export const getTabsTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TabsDesignTokens => {
  const tab = tokens?.component?.tab;
  const underline = tab?.underline;

  return {
    fontFamily,
    fontWeight: tab?.['font-weight']?.default,
    fontSize: tab?.['font-size'],
    lineHeight: tab?.['line-height'],

    marginHorizontal: underline?.['margin-right'],
    marginBottom: '-1px',
    paddingVertical: underline?.['padding-top'],
    paddingHorizontal: underline?.['padding-left'],
    paddingBottom: `calc(${underline?.['padding-top']} + 1px)`,
    iconMargin: tokens?.generic?.spacing?.[8],
    minWidth: '48px',

    borderBottom: `${underline?.['border-width']?.selected} solid transparent`,

    default: {
      color: tab?.['text-color'],
      iconColor: tab?.icon?.fill,
    },

    hover: {
      color: tab?.['text-color'],
      iconColor: tab?.icon?.fill,
      borderColor: underline?.['border-color']?.hover,
    },

    focus: {
      color: tab?.['text-color'],
      iconColor: tab?.icon?.fill,
      borderColor: underline?.['border-color']?.selected,
    },

    active: {
      color: tab?.['text-color'],
      iconColor: tab?.icon?.fill,
      fontWeight: tab?.['font-weight']?.selected,
      borderColor: underline?.['border-color']?.selected,
    },

    disabled: {
      color: tab?.['text-color'],
      iconColor: tab?.icon?.fill,
    },

    container: {
      borderBottomColor: underline?.['border-bottom-color'],
      borderBottomWidth: underline?.['border-bottom-width'],
    },

    focusShadow: {
      color: underline?.['box-shadow-color']?.focus,
      width: '0px',
      offset: underline?.['box-shadow-y']?.focus,
      blur: underline?.['box-shadow-blur']?.focus,
      spread: underline?.['box-shadow-spread']?.focus,
    },

    opacity: {
      disabled: tab?.opacity?.disabled,
    },
  };
};

/**
 * Weave 3 resolves every tab colour per state — `text-color` and `icon.fill`
 * both carry `{ default, hover, selected, selected-hover }` — where Weave 2 had
 * one flat colour for all four and Alloy hardcoded a light-theme literal. That
 * per-state map is what makes the dark themes legible (DSGN-2595).
 *
 * Geometry differs from Weave 2 in two ways, both faithful to the source:
 * the inter-tab gap comes from the tab node's own padding (W3 has no
 * `underline.margin-*`), and the rail colour comes from
 * `underline.border-color.default` rather than a dedicated
 * `underline.border-bottom-color`. The 1px rail overlap — negative
 * `margin-bottom` plus a transparent bottom border — is kept from Weave 2 so
 * the selected indicator still covers the container line.
 *
 * W3 states the disabled step as a percentage string (`'50'`), so it is scaled
 * to the CSS `opacity` ratio the component applies, matching
 * {@link getButtonTokensWeave3}.
 *
 * `focus-shadow` carries only `color`: W3 draws focus as an even ring
 * (`underline.focus-ring`), not the offset drop shadow Alloy composes from
 * `width`/`offset`, and the Weave focus path in `TabTitle` reads the colour
 * alone. `selected-hover` has no slot in {@link TabsDesignTokens} — the
 * component has no hover-while-active state — so it is deliberately unread.
 *
 * `indicator` is omitted for the same reason as in {@link getTabsTokensWeave}.
 */
export const getTabsTokensWeave3 = (
  tab?: Weave3TabsInput,
  fontFamily = 'Artifakt Element, sans-serif',
): TabsDesignTokens => {
  const underline = tab?.underline;
  const text = tab?.['text-color'];
  const icon = tab?.icon?.fill;
  const paddingVertical = tab?.['padding-top'];
  const disabledOpacity = tab?.opacity?.disabled;

  return {
    fontFamily,
    fontWeight: tab?.['font-weight']?.default,
    fontSize: tab?.['font-size'],
    lineHeight: tab?.['line-height'],

    marginHorizontal: tab?.['padding-left'],
    marginBottom: '-1px',
    paddingVertical,
    paddingHorizontal: underline?.['padding-left'],
    paddingBottom: `calc(${paddingVertical} + 1px)`,
    iconMargin: tab?.gap,
    minWidth: '48px',

    borderBottom: `${underline?.['border-width']?.selected} solid transparent`,

    default: {
      color: text?.default,
      iconColor: icon?.default,
    },

    hover: {
      color: text?.hover,
      iconColor: icon?.hover,
      borderColor: underline?.['border-color']?.hover,
    },

    focus: {
      color: text?.default,
      iconColor: icon?.default,
      borderColor: underline?.['border-color']?.selected,
    },

    active: {
      color: text?.selected,
      iconColor: icon?.selected,
      fontWeight: tab?.['font-weight']?.selected,
      borderColor: underline?.['border-color']?.selected,
    },

    disabled: {
      color: text?.default,
      iconColor: icon?.default,
    },

    container: {
      borderBottomColor: underline?.['border-color']?.default,
      borderBottomWidth: tab?.['border-width']?.default,
    },

    focusShadow: {
      color: underline?.['focus-ring']?.color,
    },

    opacity: {
      disabled: disabledOpacity
        ? String(Number.parseFloat(disabledOpacity) / 100)
        : undefined,
    },
  };
};
